import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Pagination as MuiPagination,
  PaginationItem,
  Stack,
  Typography,
  Box,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components for custom styling
const StyledPagination = styled(MuiPagination)(({ theme }) => ({
  '& .MuiPaginationItem-root': {
    margin: theme.spacing(0, 0.5),
    minWidth: '40px',
    height: '40px',
    borderRadius: theme.shape.borderRadius,
    transition: 'all 0.2s ease',
    fontWeight: 500,
    fontSize: '0.9rem',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '& .MuiPaginationItem-page.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  '& .MuiPaginationItem-ellipsis': {
    minWidth: '40px',
    height: '40px',
  },
  '& .Mui-disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
}));

const Pagination = ({
  totalItems,
  itemsPerPage,
  currentPage: externalCurrentPage,
  onPageChange,
  maxVisiblePages = 5,
  showPrevNext = true,
  showFirstLast = false,
  prevLabel = '‹',
  nextLabel = '›',
  firstLabel = '«',
  lastLabel = '»',
  disabled = false,
  className = '',
  renderPageItem,
  // Style props for customization
  containerStyle = {},
  pageInfoStyle = {},
  variant = 'outlined', // 'outlined', 'text'
  shape = 'rounded', // 'rounded', 'circular'
  size = 'medium', // 'small', 'medium', 'large'
  color = 'primary',
  showPageInfo = false,
  pageInfoPosition = 'bottom', // 'top', 'bottom', 'left', 'right'
}) => {
  // Use internal state if external currentPage is not provided
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const isControlled = externalCurrentPage !== undefined;
  const currentPage = isControlled ? externalCurrentPage : internalCurrentPage;
  
  // Calculate total pages
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  // Handle page change
  const handlePageChange = (event, page) => {
    if (disabled) return;
    
    if (!isControlled) {
      setInternalCurrentPage(page);
    }
    
    if (onPageChange) {
      onPageChange(page);
    }
  };
  
  // Get page info text
  const getPageInfoText = () => {
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);
    return `Showing ${startItem}-${endItem} of ${totalItems} items`;
  };
  
  // Don't render if no pages
  if (totalPages <= 1) {
    return null;
  }
  
  // Determine visible pages based on screen size
  const visiblePages = isMobile ? Math.min(maxVisiblePages, 3) : maxVisiblePages;
  
  // Custom renderer for pagination items if renderPageItem is provided
  const renderItem = (item) => {
    if (renderPageItem) {
      const customItem = renderPageItem({
        page: item.page,
        isActive: item.selected,
        onClick: () => handlePageChange(null, item.page),
        disabled: disabled || item.disabled,
      });
      
      if (customItem) {
        return customItem;
      }
    }
    
    // Customize label based on button type
    let label = item.page;
    if (item.type === 'previous') label = prevLabel;
    if (item.type === 'next') label = nextLabel;
    if (item.type === 'first') label = firstLabel;
    if (item.type === 'last') label = lastLabel;
    
    return (
      <PaginationItem
        {...item}
        page={item.page}
        selected={item.selected}
        disabled={disabled || item.disabled}
        sx={{
          minWidth: '40px',
          height: '40px',
          ...(item.type === 'previous' && showFirstLast && { order: 1 }),
          ...(item.type === 'first' && { order: 0 }),
          ...(item.type === 'last' && { order: 3 }),
          ...(item.type === 'next' && showFirstLast && { order: 2 }),
        }}
      >
        {label}
      </PaginationItem>
    );
  };
  
  // Create custom buttons for first/last if needed
  const getCustomPaginationItems = () => {
    if (!showFirstLast) return null;
    
    return (_, type) => {
      if (type === 'first') {
        return (
          <PaginationItem
            type="first"
            page={1}
            disabled={currentPage === 1 || disabled}
            onClick={(e) => handlePageChange(e, 1)}
            sx={{ minWidth: '40px', height: '40px' }}
          >
            {firstLabel}
          </PaginationItem>
        );
      }
      if (type === 'last') {
        return (
          <PaginationItem
            type="last"
            page={totalPages}
            disabled={currentPage === totalPages || disabled}
            onClick={(e) => handlePageChange(e, totalPages)}
            sx={{ minWidth: '40px', height: '40px' }}
          >
            {lastLabel}
          </PaginationItem>
        );
      }
      return null;
    };
  };
  
  const PageInfo = () => (
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ fontWeight: 500, ...pageInfoStyle }}
    >
      {getPageInfoText()}
    </Typography>
  );
  
  const paginationComponent = (
    <StyledPagination
      className={className}
      count={totalPages}
      page={currentPage}
      onChange={handlePageChange}
      siblingCount={Math.floor((visiblePages - 1) / 2)}
      boundaryCount={showFirstLast ? 1 : 0}
      variant={variant}
      shape={shape}
      size={size}
      color={color}
      disabled={disabled}
      renderItem={renderItem}
      sx={{
        '& .MuiPagination-ul': {
          flexWrap: 'wrap',
          justifyContent: 'center',
        },
        ...containerStyle,
      }}
    />
  );
  
  // Determine layout based on pageInfoPosition
  const getLayout = () => {
    switch (pageInfoPosition) {
      case 'top':
        return (
          <Stack spacing={2} alignItems="center">
            <PageInfo />
            {paginationComponent}
          </Stack>
        );
      case 'bottom':
        return (
          <Stack spacing={2} alignItems="center">
            {paginationComponent}
            <PageInfo />
          </Stack>
        );
      case 'left':
        return (
          <Stack direction="row" spacing={2} alignItems="center">
            <PageInfo />
            {paginationComponent}
          </Stack>
        );
      case 'right':
        return (
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            {paginationComponent}
            <PageInfo />
          </Stack>
        );
      default:
        return paginationComponent;
    }
  };
  
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        margin: theme.spacing(4, 0),
      }}
    >
      {showPageInfo ? getLayout() : paginationComponent}
    </Box>
  );
};

Pagination.propTypes = {
  totalItems: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
  maxVisiblePages: PropTypes.number,
  showPrevNext: PropTypes.bool,
  showFirstLast: PropTypes.bool,
  prevLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  nextLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  firstLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  lastLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  disabled: PropTypes.bool,
  className: PropTypes.string,
  renderPageItem: PropTypes.func,
  // MUI specific props
  variant: PropTypes.oneOf(['outlined', 'text']),
  shape: PropTypes.oneOf(['rounded', 'circular']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  color: PropTypes.oneOf(['primary', 'secondary', 'standard']),
  showPageInfo: PropTypes.bool,
  pageInfoPosition: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  // Style props
  containerStyle: PropTypes.object,
  pageInfoStyle: PropTypes.object,
};

export default Pagination;